import React from "react";
import { useTheme } from "@mui/material";
import { Icon } from "../Icon";
import { StyledAvatar } from "./Avatar.styled";

const TYPES = {
  PLAYER: "player",
  GROUP: "group",
  TEAM: "team",
  ACTION: "action",
  TIME_PERIOD: "time period",
  GAME: "game",
} as const;

const TYPES_PLACEHOLDER_ICONS = {
  [TYPES.PLAYER]: "person",
  [TYPES.GROUP]: "groups",
  [TYPES.TEAM]: "WscIconTeam",
  [TYPES.ACTION]: "interests",
  [TYPES.TIME_PERIOD]: "calendar_today",
  [TYPES.GAME]: "calendar_month",
};

export type TypesValue = (typeof TYPES)[keyof typeof TYPES];
type AvatarSizes = "medium" | "big" | "huge" | "xl" | "xxl";
type AvatarVariants = "circular" | "rounded" | "square";

export interface AvatarProps {
  /**
   * The alt attribute provides alternative information for an image if a user for some reason cannot view it.
   */
  alt: string;
  /**
   * Link to avatar src. Specifies the path to the image.
   */
  src?: string | null;
  /**
   * Type of avatar (one of "player"/"group"/"team"/"action"/"time period"/"game"), used to show relevant placeholder in case when src is empty or invalid.
   */
  type?: TypesValue;
  /**
   * The shape of the avatar.
   */
  variant?: AvatarVariants;
  /**
   * Shows green border if avatar highlighted.
   */
  isHighlighted?: boolean;
  /**
   * Size of avatar, medium = spacing[2] x 1.5 (24px diameter), big = spacing[2] x 2.5 (40px diameter),
   * huge = spacing[2] x 3.75 (60px diameter), xl = spacing[2] x 5 (80px diameter), xxl = spacing[2] x 6.245 (100px diameter).
   */
  size?: AvatarSizes;
}

const getIconSize = (size: AvatarSizes) => {
  if (size === "xxl") {
    return "xl";
  }

  if (size === "huge") {
    return "big";
  }

  return size;
};

function Avatar({
  src,
  alt,
  type = TYPES.PLAYER,
  variant = "circular",
  isHighlighted = false,
  size = "xl",
}: AvatarProps) {
  const placeholderIcon = TYPES_PLACEHOLDER_ICONS[type];

  const theme = useTheme();

  return src ? (
    <StyledAvatar
      src={src}
      size={size}
      variant={variant}
      alt={alt}
      isHighlighted={isHighlighted}
      data-testid={`avatar-${alt}`}
    />
  ) : (
    <StyledAvatar
      isPlaceholder
      data-testid={`avatar-placeholder-${alt}`}
      size={size}
      variant={variant}
      alt={alt}
      isHighlighted={isHighlighted}
    >
      <Icon
        name={placeholderIcon}
        size={getIconSize(size)}
        color={theme.wsc.palette.neutral[300]}
      />
    </StyledAvatar>
  );
}

export default Avatar;
